// src/index.js

import 'react-app-polyfill/stable'
import 'core-js'
import React from 'react'
import { createRoot } from 'react-dom/client'
import { Provider } from 'react-redux'
import { BrowserRouter } from 'react-router-dom'
import { EventType, InteractionStatus, PublicClientApplication } from '@azure/msal-browser'
import { MsalProvider, useMsal } from '@azure/msal-react'

import App from './App'
import reportWebVitals from './reportWebVitals'
import store from './store'
import {
  msalConfig,
  clearOldMsalCacheIfNeeded,
  clearAppUserStorage,
  getPrimaryAccount,
  isPrimaryAccount,
} from './components/auth/authConfig'
import { AuthProvider } from './contexts/AuthContext'

/*
 * React lazy chunk recovery.
 *
 * This fixes cases where a user has an old main.js cached after deployment,
 * and that old main.js tries to load a chunk file that no longer exists.
 *
 * Example:
 * ChunkLoadError: Loading chunk 2087 failed.
 */
const CHUNK_LOAD_RELOAD_KEY = 'ivetted.chunkLoadReloadedAt'
const CHUNK_LOAD_RELOAD_COOLDOWN_MS = 5 * 60 * 1000

function getChunkLoadErrorMessage(error) {
  return [
    error?.name,
    error?.message,
    error?.errorMessage,
    error?.reason?.name,
    error?.reason?.message,
    error?.target?.src,
    error?.target?.href,
  ]
    .filter(Boolean)
    .join(' ')
}

function isChunkLoadError(error) {
  const message = getChunkLoadErrorMessage(error).toLowerCase()

  return (
    message.includes('chunkloaderror') ||
    message.includes('loading chunk') ||
    message.includes('loading css chunk') ||
    message.includes('css_chunk_load_failed') ||
    message.includes('.chunk.js') ||
    message.includes('.chunk.css')
  )
}

function hasRecentlyReloadedForChunkError() {
  const lastReloadedAt = Number(sessionStorage.getItem(CHUNK_LOAD_RELOAD_KEY))

  if (!lastReloadedAt) {
    return false
  }

  return Date.now() - lastReloadedAt < CHUNK_LOAD_RELOAD_COOLDOWN_MS
}

function handleChunkLoadError(error, event) {
  if (!isChunkLoadError(error)) {
    return
  }

  if (hasRecentlyReloadedForChunkError()) {
    console.error('Chunk load failed again after automatic reload.', error)
    return
  }

  event?.preventDefault?.()

  sessionStorage.setItem(CHUNK_LOAD_RELOAD_KEY, Date.now().toString())

  /*
   * Use replace so the broken route is not duplicated in browser history.
   */
  window.location.replace(window.location.href)
}

window.addEventListener('error', (event) => {
  handleChunkLoadError(event.error || event, event)
})

window.addEventListener('unhandledrejection', (event) => {
  handleChunkLoadError(event.reason || event, event)
})

clearOldMsalCacheIfNeeded()

export const msalInstance = new PublicClientApplication(msalConfig)

const loading = (
  <div className="pt-3 text-center">
    <div className="sk-spinner sk-spinner-pulse"></div>
  </div>
)

function setActiveAccount(account) {
  if (account && isPrimaryAccount(account)) {
    msalInstance.setActiveAccount(account)
    return
  }

  msalInstance.setActiveAccount(null)
}

function setInitialActiveAccount() {
  const primaryAccount = getPrimaryAccount(msalInstance)
  setActiveAccount(primaryAccount)
}

function ApplicationAfterMsalRedirectProcessing() {
  const { instance, accounts, inProgress } = useMsal()

  const activeAccount = React.useMemo(() => {
    return getPrimaryAccount(instance)
  }, [instance, accounts])

  React.useEffect(() => {
    if (activeAccount) {
      instance.setActiveAccount(activeAccount)
      return
    }

    if (instance.getActiveAccount()) {
      instance.setActiveAccount(null)
    }
  }, [instance, activeAccount])

  /*
   * Do not mount AuthProvider or routes while MSAL is still completing
   * login/acquire-token redirect handling.
   */
  if (inProgress !== InteractionStatus.None) {
    return loading
  }

  return (
    <AuthProvider msalInstance={instance} activeAccount={activeAccount}>
      <App />
    </AuthProvider>
  )
}

function registerMsalEventCallbacks() {
  msalInstance.addEventCallback((event) => {
    if (
      (event.eventType === EventType.LOGIN_SUCCESS ||
        event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
        event.eventType === EventType.SSO_SILENT_SUCCESS) &&
      event.payload?.account
    ) {
      /*
       * Only set the active account if it belongs to the current app login policy.
       * This prevents an old signup/dev/previous-policy account being reused.
       */
      if (isPrimaryAccount(event.payload.account)) {
        setActiveAccount(event.payload.account)
      }

      return
    }

    if (event.eventType === EventType.LOGOUT_SUCCESS) {
      clearAppUserStorage()
      msalInstance.setActiveAccount(null)
    }
  })
}

function renderApp() {
  createRoot(document.getElementById('root')).render(
    <MsalProvider instance={msalInstance}>
      <BrowserRouter>
        <Provider store={store}>
          <ApplicationAfterMsalRedirectProcessing />
        </Provider>
      </BrowserRouter>
    </MsalProvider>,
  )

  reportWebVitals()
}

async function initialiseApplication() {
  await msalInstance.initialize()

  registerMsalEventCallbacks()
  setInitialActiveAccount()

  renderApp()
}

initialiseApplication().catch((error) => {
  console.error('Unable to initialise authentication.', error)
})
